import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "comebye.theme";

interface ThemeContextValue {
  /** The theme actually applied to the document right now. */
  theme: ThemeMode;
  /** Whether the operator has pinned a theme, or it's still following the OS. */
  isPinned: boolean;
  /** Flip between light and dark, pinning the choice (persisted). */
  toggleTheme: () => void;
  /** Drop the pinned choice and follow the OS preference again. */
  followSystem: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredTheme(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Storage can be unavailable (private mode, disabled); fall back silently.
    return null;
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pinned, setPinned] = useState<ThemeMode | null>(() => readStoredTheme());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const theme: ThemeMode = pinned ?? (systemDark ? "dark" : "light");

  useEffect(() => {
    const root = document.documentElement;
    if (pinned) {
      root.setAttribute("data-theme", pinned);
    } else {
      root.removeAttribute("data-theme");
    }
  }, [pinned]);

  const toggleTheme = useCallback(() => {
    setPinned((current) => {
      const next: ThemeMode = (current ?? (systemDark ? "dark" : "light")) === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Non-fatal — the toggle still works for this session.
      }
      return next;
    });
  }, [systemDark]);

  const followSystem = useCallback(() => {
    setPinned(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Non-fatal.
    }
  }, []);

  const value = useMemo(
    () => ({ theme, isPinned: pinned !== null, toggleTheme, followSystem }),
    [theme, pinned, toggleTheme, followSystem],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
